import { dispatchSentryException } from '@btcdirect/functions';
import { retry } from '@btcdirect/shared/src/composables/api/config/retry';
import { Endpoints } from '@btcdirect/shared/src/composables/api/endpoints';
import { createException } from '@btcdirect/shared/src/composables/api/exception/exception';
import { ApiErrorCodes, ApiResponseException, RefreshTokensResponse } from '@btcdirect/shared/src/composables/api/types';
import { useEnv } from '@btcdirect/shared/src/composables/util/env';
import { useAuthenticationStore, useConfigurationStore } from '@btcdirect/stores';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useConfiguration } from '../../configuration';

const { getString } = useEnv();

export const baseURL = import.meta.env.VITE_OVERRIDE_API_ENDPOINT || getString('VITE_API_ENDPOINT', 'https://api.btcdirect.eu/api');

/**
 * Instance of axios
 */
const api = axios.create({ baseURL });

/**
 * Interceptor used to set the necessary headers for each call.
 * When a user identifier is provided, it takes precedence over the authorization token.
 */
api.interceptors.request.use(
    (config) => {
        const authenticationStore = useAuthenticationStore();
        const instanceConfiguration = useConfigurationStore();
        const { version, type } = useConfiguration();

        config.headers = Object.assign({}, config.headers, { 'X-API-Key': authenticationStore.client.token, Accept: 'application/json' });

        if (authenticationStore.user.tokens.identifier) {
            config.headers['User-Identifier'] = authenticationStore.user.tokens.identifier;
            return config;
        }

        if (authenticationStore.user.tokens.authorization) {
            config.headers['User-Authorization'] = `Bearer ${authenticationStore.user.tokens.authorization}`;
        }

        config.headers['BTCD-Session-Identifier'] = instanceConfiguration.instanceConfiguration?.sessionIdentifier ?? null;
        config.headers['BTCD-Widget-Version'] = version.value ?? null;
        config.headers['BTCD-Widget-Type'] = type.value ?? null;
        config.headers['BTCD-Widget-Integration'] = instanceConfiguration.isUnifiedCheckout ? 'unified-checkout' : 'embedded';

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Interceptor used to catch any errors and, if needed, format them into a response we can expect.
 * If the error is about the auth token being expired, then refresh the token and try the request again.
 */
api.interceptors.response.use(
    (response: AxiosResponse): Promise<AxiosResponse> => response.data,
    async (error: AxiosError): Promise<AxiosResponse | ApiResponseException> => {
        const authenticationStore = useAuthenticationStore();

        // Get the exception in the correct format
        const exception = getException(error);

        if (isInvalidAuthorizationTokenException(exception)) {
            authenticationStore.setUserTokens({ refresh: null, authorization: null });
            return Promise.reject(exception);
        }

        if (isExpiredOrInvalidRefreshToken(exception)) {
            authenticationStore.setUserTokens({ refresh: null, authorization: null });
            delete error.config.headers['User-Authorization'];
            return Promise.reject(exception);
        }

        if (!isExpiredAuthorizationTokenException(exception)) {
            return Promise.reject(exception);
        }

        return refreshAndRetry(error.config);
    }
);

/**
 * Refresh the user token and retry the original call.
 * @param config
 */
async function refreshAndRetry(config: AxiosRequestConfig): Promise<AxiosResponse> {
    const authenticationStore = useAuthenticationStore();

    try {
        const { token, refreshToken } = await refresh();
        authenticationStore.setUserTokens({ authorization: token, refresh: refreshToken });

        config.headers['User-Authorization'] = `Bearer ${token}`;
        return retry(config);
    } catch (exception) {
        return Promise.reject(exception);
    }
}

/**
 * Refresh the user token using the refresh token.
 */
async function refresh(): Promise<RefreshTokensResponse> {
    const authenticationStore = useAuthenticationStore();

    if (!authenticationStore.user.tokens.refresh) {
        const exception = createException('Cannot refresh the user authentication. No refresh token found.', ApiErrorCodes.ER700, 400);
        return Promise.reject(exception);
    }

    try {
        return await api.post(Endpoints.REFRESH_TOKEN, { refreshToken: authenticationStore.user.tokens.refresh });
    } catch (exception) {
        return Promise.reject(exception);
    }
}

/**
 * Convert the response error to an exception we can expect.
 * @param error
 */
function getException(error: AxiosError): ApiResponseException {
    // When the error is already correctly formatted, run with it.
    if (error.isAxiosError && (<ApiResponseException>error.response)?.data?.errors) {
        return error.response as ApiResponseException;
    }

    // Create an exception with the correct format
    const exception = createException(error.message);
    exception.isCancelled = axios.isCancel(error);

    if (exception.isCancelled) {
        return exception;
    }

    dispatchSentryException('An unexpected error occurred: ' + error.message);
    return exception;
}

/**
 * Check if the authorization token is invalid.
 * @param exception
 */
function isInvalidAuthorizationTokenException(exception: ApiResponseException): boolean {
    return Object.values(exception.data.errors).some((error) => ApiErrorCodes.ER702 === error.code);
}

/**
 * Check if the (user) authorization token is expired.
 * @param exception
 */
function isExpiredAuthorizationTokenException(exception: ApiResponseException): boolean {
    return Object.values(exception.data.errors).some((error) => [ApiErrorCodes.ER701, ApiErrorCodes.ER800, ApiErrorCodes.ER801].includes(error.code));
}

/**
 * Check if the refresh token is expired or invalid.
 * @param exception
 */
function isExpiredOrInvalidRefreshToken(exception: ApiResponseException): boolean {
    return Object.values(exception.data.errors).some((error) => [ApiErrorCodes.ER036, ApiErrorCodes.ER037].includes(error.code));
}

export default api;
